/**
 * Single source of truth for the current Terms of Service and Privacy Policy versions.
 *
 * Bumping either constant triggers the in-app re-acceptance dialog for every existing user
 * whose profile records a stale value. New signups always record the current values.
 *
 * Use ISO date strings (YYYY-MM-DD) corresponding to the publication date of the policy
 * in the marketing site's Astro pages.
 */

export const CURRENT_TERMS_VERSION = '2026-04-27';
export const CURRENT_PRIVACY_VERSION = '2026-04-27';

export const TERMS_URL = 'https://elara.travel/terms';
export const PRIVACY_URL = 'https://elara.travel/privacy';

/**
 * The subset of a user profile needed to decide whether re-acceptance is required.
 * Structurally a slice of `UserProfileData`, kept narrow so both the client state
 * store and the server access gate can call the predicate without importing the
 * full model spec.
 */
export interface PolicyAcceptanceFields {
  onboardingCompletedAt?: string;
  acceptedTermsVersion?: string;
  acceptedPrivacyVersion?: string;
}

/**
 * Single source of truth for the policy re-acceptance staleness predicate.
 *
 * Returns true iff the user has finished onboarding but their recorded acceptance
 * versions are stale (or absent) relative to the current domain-wide constants.
 *
 * The `onboardingCompletedAt` gate prevents this from interrupting the
 * signup -> onboarding flow, where acceptance is captured via the signup form
 * checkbox and recorded at profile creation.
 *
 * Shared between the client (inline blocking dialog) and the server (defense-in-depth
 * access gate on sensitive flows) so the two can never drift apart.
 */
export function requiresPolicyReacceptance(profile: PolicyAcceptanceFields): boolean {
  if (!profile.onboardingCompletedAt) return false;
  return (
    profile.acceptedTermsVersion !== CURRENT_TERMS_VERSION ||
    profile.acceptedPrivacyVersion !== CURRENT_PRIVACY_VERSION
  );
}
